import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../utils/appConfirm';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../styles/SalesPipelineSummary.css';
import { graphqlRequest } from '../utils/graphqlRequest';

const PIPELINE_OVERVIEW_QUERY = `
  query FindAllPipelineOverview {
    findAllPipelineOverview {
      id
      name
      description
      order
      totalDeals
      stages {
        id
        name
        order
        dealCount
      }
    }
  }
`;

const REMOVE_PIPELINE = `
  mutation RemovePipeline($id: String!) {
    removePipeline(id: $id)
  }
`;

export default function SalesPipelineSummary({ onRefreshDashboard }) {
  const location = useLocation();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [localSearch, setLocalSearch] = useState('');

  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (localSearch || params.get('search') || '').trim().toLowerCase();
  }, [location.search, localSearch]);

  const fetchPipelines = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const data = await graphqlRequest(PIPELINE_OVERVIEW_QUERY);
      setPipelines(data?.findAllPipelineOverview || []);
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      toast.error(err?.message || 'Failed to load pipelines');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipelines();
    const refresh = () => fetchPipelines(true);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [fetchPipelines]);

  const visiblePipelines = useMemo(() => {
    if (!searchQuery) return pipelines;
    return pipelines.filter((pipeline) => {
      const stageText = (pipeline.stages || [])
        .map((s) => s?.name || '')
        .join(' ');
      return [pipeline.name, pipeline.description, stageText].some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(searchQuery),
      );
    });
  }, [pipelines, searchQuery]);

  const handleDeletePipeline = async (pipeline) => {
    const name = pipeline?.name || 'this pipeline';
    const ok = await appConfirmMessage(
      `Delete "${name}"?\n\nThis will also remove its stages. This cannot be undone.`,
    );
    if (!ok || !pipeline?.id) return;

    setDeletingId(pipeline.id);
    try {
      const data = await graphqlRequest(REMOVE_PIPELINE, { id: pipeline.id });
      if (!data?.removePipeline) {
        throw new Error('Pipeline could not be deleted');
      }
      setPipelines((prev) => prev.filter((p) => p.id !== pipeline.id));
      toast.success(`Deleted "${name}"`);
      onRefreshDashboard?.();
    } catch (err) {
      console.error('Delete pipeline failed', err);
      toast.error(err?.message || 'Failed to delete pipeline');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="pipeline-summary-container">
      <div className="pipeline-summary-toolbar">
        <label className="pipeline-summary-search">
          <span className="sr-only">Search pipelines or stages</span>
          <input
            type="search"
            value={localSearch}
            onChange={(event) => setLocalSearch(event.target.value)}
            placeholder="Search pipelines or stages…"
          />
        </label>
      </div>

      {loading ? <p className="pipeline-summary-empty">Loading pipelines...</p> : null}
      {!loading && pipelines.length === 0 ? (
        <div className="pipeline-summary-empty">
          <p>No pipeline data found. Create a new pipeline to get started.</p>
        </div>
      ) : null}
      {!loading && searchQuery && visiblePipelines.length === 0 ? (
        <div className="pipeline-summary-empty">
          <p>No pipelines match &quot;{searchQuery}&quot;.</p>
        </div>
      ) : null}
      {searchQuery ? (
        <p className="pipeline-summary-filter-hint">
          Filtered by &quot;{searchQuery}&quot; — {visiblePipelines.length} match
          {visiblePipelines.length === 1 ? '' : 'es'}
        </p>
      ) : null}
      {!loading && visiblePipelines.map((pipeline) => (
        <div key={pipeline.id} className="pipeline-overview-card">
          <div className="pipeline-summary-header">
            <h3>{pipeline.name} Overview</h3>
            <button
              type="button"
              className="pipeline-delete-btn"
              disabled={deletingId === pipeline.id}
              onClick={() => handleDeletePipeline(pipeline)}
              title="Delete pipeline"
            >
              {deletingId === pipeline.id ? 'Deleting…' : 'Delete'}
            </button>
          </div>

          <div className="pipeline-summary-stats">
            <div className="summary-item">
              <p className="summary-label">Sales stages</p>
              <p className="summary-value">{pipeline.stages.length}</p>
            </div>
            <div className="summary-item">
              <p className="summary-label">Average opportunities per stage</p>
              <p className="summary-value">
                {pipeline.stages.length
                  ? (pipeline.totalDeals / pipeline.stages.length).toFixed(1)
                  : '0'}
              </p>
            </div>
            <div className="summary-item">
              <p className="summary-label">Open opportunities</p>
              <p className="summary-value">{pipeline.totalDeals}</p>
            </div>
          </div>

          <div className="pipeline-stage-list">
            {[...pipeline.stages]
              .sort((a, b) => Number(a.order) - Number(b.order))
              .map((stage) => (
              <div key={stage.id} className="stage-card">
                <p className="stage-title">{stage.name}</p>
                <p className="stage-count">{stage.dealCount} opportunities</p>
              </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
